/**
 * The page behind the QR sticker on a machine. No login, no account, no app.
 *
 * Someone walks up to a bucket truck, points their phone camera at a sticker, and this opens. They
 * type a badge number and a PIN, fill in the sheet, take photos, and hit send. That is the whole
 * product.
 *
 * Three deliberate departures from the rest of the front end
 * ----------------------------------------------------------
 * 1. **It does not use `formsService` / the shared api client.** That client's request interceptor
 *    calls `ensureFreshToken()`, which fires a `POST /auth/refresh` for an anonymous visitor, and its
 *    401 handler can redirect to `/login` — a bad outcome for someone standing in a yard who has no
 *    login. Plain `fetch`, exactly like `ClientApproval.jsx`, the existing public page.
 *
 * 2. **It does not use `CanvasRenderer`.** The canvas keeps the column count the sheet was DESIGNED
 *    at and scales it to the container, so a form laid out on a desktop renders about a third size on
 *    a phone. Every inspection sheet is phone-only, so this renders the fields as a single stacked
 *    column, sorted by their position on the designed canvas.
 *
 * 3. **There is no name picker, and there must never be one.** Letting the hand choose their name
 *    from a list would publish the tenant's entire employee directory to anyone who photographs a
 *    sticker in a car park. Identity is typed and verified server-side; the server sends back the
 *    name only after the credential checks out.
 *
 * 4. **The language control is on the page, not in a user setting.** A field hand has no login: they
 *    are anonymous until they type a badge and a PIN, and they have to READ "Who are you? / Badge
 *    number / PIN" to do that. So a per-user preference could never reach them, and the control has to
 *    be visible before anyone has identified themselves — which is why it sits above the equipment
 *    card, the first thing on the page. Nothing about the language reaches the server: every language
 *    the sheet is written in arrives in the one response, so switching is instant and a submission is
 *    byte-identical whichever language it was filled in.
 *
 * 5. **The sheet's translations arrive BESIDE its schema, not inside it.** `GET /inspection/...`
 *    returns `schema` and `translations` as siblings, and `renderablesOf` merges the second onto the
 *    render objects it derives from the first. The strings used to live inside `schema_json`, which
 *    made a translated document unparseable to any older image — and on THIS page a parse failure is
 *    404 "That sticker isn't valid. Check with your foreman." for every sticker in the tenant. The
 *    fetched schema is the stored document; nothing here writes translations back into it.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import useSignatureCanvas, { SIGNATURE_CANVAS_STYLE } from '../../../shared/hooks/useSignatureCanvas'

import {
  CHROME, bundleLanguages, chrome, claimDocumentLanguage, documentNode, hydrate, languageName,
  pickLanguage, releaseDocumentLanguage, rememberLanguage, serverMessage, tDocumentName, tHelp,
  tLabel, tOptions, tRow, tText,
} from '../i18n'

const API_BASE = import.meta.env.VITE_FORMS_API_URL || '/api/forms'

// Types this page can draw. Anything else (tables, directory pickers) is skipped rather than
// half-rendered — a field a person cannot answer properly is worse than one they never see.
//
// This set is a HAND-MAINTAINED duplicate of the backend field registry, and it fails by omission: a
// type missing here is filtered out of `fields` entirely, so a required answer has no box, Send 422s,
// and the highlight loop below maps over the array the field was removed from — "some answers are still
// needed" with nothing marked. tests/test_field_type_renderer_parity.py watches this set against the
// registry, and every deliberate exclusion is written down there with its reason.
//
// It is also what decides whether a sticker may be PRINTED at all: sticker_submit.PUBLIC_UNRENDERABLE_TYPES
// is this set's complement, and it refuses a QR for any form requiring a type listed there. So wiring a
// widget in here shrinks that refusal in the same commit — the parity test fails until it does.
const RENDERABLE = new Set([
  'short_text', 'long_text', 'integer', 'decimal', 'date', 'time', 'checkbox',
  'single_select', 'multi_select', 'checklist', 'signature', 'section_note', 'photo', 'attachment',
  'computed',
])

const wrap = { maxWidth: 560, margin: '0 auto', padding: '16px', fontFamily: 'system-ui, sans-serif' }
const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 12 }
const label = { display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 15 }
// 16px minimum on inputs: anything smaller makes iOS Safari zoom on focus, which on a phone in a yard
// means the person loses their place in the form every time they tap a box.
const input = { width: '100%', padding: '12px', fontSize: 16, border: '1px solid #cbd5e1', borderRadius: 8, boxSizing: 'border-box' }
const button = { width: '100%', padding: '14px', fontSize: 17, fontWeight: 600, border: 0, borderRadius: 10, background: '#0f2c4d', color: '#fff' }
// A checklist choice is tapped one-handed, outdoors, often through a glove. 44px is the iOS minimum
// target size and 16px is the no-zoom floor (same reason as `input` above). The selected state is a
// FILLED swap rather than a border tint — a tint is unreadable in direct sun on a smeared screen — and
// it is the SAME fill for every choice: the option set is tenant data, so colouring "Defect" red would
// hardcode one tenant's vocabulary into a shared renderer.
const choice = { flex: 1, minHeight: 44, padding: '10px 6px', fontSize: 16, fontWeight: 600,
                 border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#334155' }
const choiceOn = { background: '#0f2c4d', color: '#fff', borderColor: '#0f2c4d' }
// A secondary action — same 44px floor and 16px no-zoom floor as `choice`, but outlined rather than
// filled so it never reads as the thing to press next. Send is the only filled button on the sheet.
const minorButton = { minHeight: 44, padding: '8px 16px', fontSize: 16, fontWeight: 600,
                      border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#334155' }

/** A v1 section's heading, as the one `text` shape this renderer draws. Its translation slot is
 *  `title` rather than `text`, so the entry is remapped here instead of teaching the renderer a second
 *  shape. */
function sectionBand(section, translations, { help = false } = {}) {
  const body = help ? section.help : section.title
  if (!body) return null
  const band = { kind: 'text', text: body, bold: !help, font_size: help ? 12 : 15 }
  return hydrate(band, translations, `section:${section.key}`, (e) => {
    const translated = help ? e.help : e.title
    if (!translated) return null
    // The recorded source is remapped with it. Dropping it here would leave the heading "unknown",
    // which the resolver TRUSTS — so a section retitled in English would keep showing the old Spanish,
    // which is the one thing the whole `_src` mechanism exists to stop.
    const src = e._src && (help ? e._src.help : e._src.title)
    return src ? { text: translated, _src: { text: src } } : { text: translated }
  })
}

/**
 * Everything this page draws, in the order it is drawn.
 *
 * ⚠ `kind: "text"` elements are IN this list, and that is a behaviour change worth stating. The old
 * filter kept `kind === 'field'` only, so every static element the designer placed was served to the
 * phone and then silently dropped: the seven section bands, the 49 CFR 396 note, the attestation the
 * operator signs — and, most seriously, the out-of-service warning ("Answering 'no' takes the machine
 * out of service and notifies the fleet manager…"). That paragraph is what makes the document a safety
 * record, and it was invisible in English. Translating it would have changed nothing while this filter
 * stood.
 *
 * `visible_if` is still not evaluated here, on either kind. That is pre-existing: the conditional
 * checklist grids already render unconditionally and the server skips hidden fields when it decides
 * what is required, so bands and grids stay consistent with each other.
 */
function renderablesOf(schema, translations) {
  if (!schema) return []
  if (schema.schema_version === 2) {
    return [...(schema.elements || [])]
      .filter((el) => (el.kind === 'field' && el.key) || (el.kind === 'text' && el.text))
      // Reading order on the designed canvas: down the page, then across.
      .sort((a, b) => (a.gy - b.gy) || (a.gx - b.gx))
      // THE MERGE POINT, and the only one. Every t*() call below reads `node.i18n`, so hydrating the
      // derived render objects here leaves all ~20 of them untouched — and leaves the FETCHED schema
      // untouched too, which is what matters: that object is the stored document, and putting the
      // strings back into it is precisely the coupling the side table removes.
      .map((el) => hydrate(el, translations))
  }
  return (schema.sections || []).flatMap((s) => [
    sectionBand(s, translations), sectionBand(s, translations, { help: true }),
    ...(s.fields || []).map((f) => hydrate(f, translations)),
  ].filter(Boolean))
}

/**
 * The drawn signature, captured on the phone that is already in the person's hand.
 *
 * **It is a field VALUE, not an upload.** What is stored is `canvas.toDataURL('image/png')` — the same
 * string the in-app pad (timekeeping/components/SignaturePad.jsx, via forms/components/FieldInput.jsx)
 * writes — and it travels inside the `payload` JSON this page already posts, NOT in the `files` part
 * beside the photos. Routing it through the upload path would look tempting (the multipart is right
 * there, and a canvas blob is a real PNG the server would accept) and it would store an attachment
 * REFERENCE list under the field key instead of a string. The same signature field would then hold a
 * string when a foreman files the sheet on a tablet and a list of dicts when a hand files it at the
 * machine; services/pdf.py renders the string and prints "Not signed" for the list, so the archived
 * copy of a safety record would be wrong — and wrong only on the unauthenticated side, where nobody is
 * watching. One field, one shape, both doors.
 *
 * A component rather than a branch inside `Field` because a canvas needs a ref and an effect, and hooks
 * cannot live inside a conditional branch.
 *
 * Exported so it can be driven directly by tests/signature_pad.test.js — the pointer and transform
 * arithmetic below has failure modes that no source scan can see.
 */
export function SignaturePad({ field, value, onChange, lang }) {
  const t = chrome(lang)
  const req = field.required === true
  // THE ARITHMETIC LIVES IN shared/hooks/useSignatureCanvas. It was written here first, on the one
  // surface where getting it wrong costs a walk back to the machine, and then extracted so the
  // client-facing signing pages inherit it instead of re-deriving it. What stays here is this
  // page's own chrome: its labels, its help text, its inline style vocabulary.
  const { canvasRef, handlers, clear, touched, signed } = useSignatureCanvas({ value, onChange })

  const onClear = () => { clear(); }

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Clear lives up here on the label line, not under the pad. Under the pad is where a thumb rests
          while the other hand steadies the phone, and a mis-tap there costs the signature. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ ...label, marginBottom: 0 }}>
          {tLabel(field, lang)}{req && <span style={{ color: '#b91c1c' }}> *</span>}
        </div>
        <button type="button" style={minorButton} onClick={onClear}>{t('clear')}</button>
      </div>
      {tHelp(field, lang) && (
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>{tHelp(field, lang)}</div>
      )}
      <div style={{ position: 'relative', marginTop: 6, background: '#fff',
                    border: '1px solid #cbd5e1', borderRadius: 8 }}>
        {!signed && !touched && (
          <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 16,
                        // Behind the finger, never in front of it.
                        pointerEvents: 'none' }}>
            {t('signHere')}
          </div>
        )}
        <canvas
          ref={canvasRef}
          {...handlers}
          aria-label={tLabel(field, lang)}
          // 170px is a comfortable one-handed signing height and still leaves plenty of sheet to
          // scroll the page from. `touchAction: none` comes from SIGNATURE_CANVAS_STYLE and belongs
          // on this element and no other — see the hook.
          style={{ ...SIGNATURE_CANVAS_STYLE, height: 170, borderRadius: 8 }}
        />
      </div>
      {/* This page runs NO client-side validation (see the checklist counter below for the same
          reason), so this line is the only confirmation the person gets that the mark registered. */}
      <div style={{ fontSize: 13, color: signed ? '#16a34a' : '#64748b', marginTop: 6 }}>
        {signed ? t('signed') : t('notSignedYet')}
      </div>
    </div>
  )
}

/**
 * A static heading / paragraph / band the designer placed on the sheet.
 *
 * Deliberately renders the tenant's own `color` / `background`: both are schema-validated to a plain
 * hex literal (COLOR_RE in form_schema.py), so nothing but an inert color can reach the CSS value
 * stream. The out-of-service warning is a red band, and printing it as unstyled grey body text would
 * lose the one visual cue that says this sentence takes a machine off the job.
 */
function TextBlock({ element, lang }) {
  const body = tText(element, lang)
  if (!body) return null
  const banded = !!element.background
  // `box` is the ONE layout property this page honours, and it earns its place: the out-of-service
  // warning is authored as a red callout — `border: 1px solid var(--alert)` on the paper sheet — and
  // without the border it is a pink paragraph. Everything else the primitive carries (label_position,
  // gap, the whole grid) is correctly ignored here: this page is a single 560px phone column by
  // construction (see renderablesOf), which is the degradation a narrow screen needs anyway.
  const box = element.box === 'outline'
    ? { border: `1px solid ${element.color || '#c9cdd2'}`, padding: '10px 12px' }
    : element.box === 'rule'
      ? { borderLeft: `3px solid ${element.color || '#111827'}`, paddingLeft: 12 }
      : null
  return (
    <p style={{
      margin: banded ? '10px 0' : '14px 0 6px',
      padding: banded ? '10px 12px' : 0,
      borderRadius: element.box ? 0 : (banded ? 8 : 0),
      background: element.background || undefined,
      color: element.color || (banded ? '#111827' : '#334155'),
      ...(box || {}),
      // 15px floor: the designed sizes go down to 11px, which is a caption on a desktop sheet and
      // unreadable at arm's length in a yard. The sheet is phone-only here, so the design's own
      // hierarchy is kept (bold, colour, band) but never at the cost of legibility.
      fontSize: Math.max(15, element.font_size || 15),
      fontWeight: element.bold ? 700 : 400,
      textAlign: element.align || 'left',
      whiteSpace: 'pre-wrap',
    }}>
      {body}
    </p>
  )
}

/**
 * @param {boolean} locked   this answer came from the sticker and the person may not change it
 * @param {boolean} mustFill this box names the machine on a BLANK sticker, so it is required here
 *                           whatever the schema says — nothing else on that record identifies the
 *                           equipment
 */
function Field({ field, value, onChange, onFiles, lang, locked = false, mustFill = false }) {
  const t = chrome(lang)
  const req = field.required === true || mustFill
  // A locked box has to LOOK unavailable as well as behave that way, or the person taps it, gets no
  // caret, and concludes the page is broken in a car park with no support channel.
  const common = {
    id: field.key,
    style: locked ? { ...input, background: '#f1f5f9', color: '#334155' } : input,
    ...(locked ? { readOnly: true, 'aria-readonly': true } : null),
  }
  if (field.type === 'section_note') {
    return <p style={{ margin: '18px 0 6px', fontWeight: 700, fontSize: 16 }}>{tLabel(field, lang)}</p>
  }
  if (field.type === 'computed') return null

  // Self-contained for the same reason the checklist below is, plus one of its own: the shared wrapper's
  // <label htmlFor={field.key}> would point at a <canvas>, which is not a labelable control, so the
  // association would be a lie to a screen reader as well as useless to a tap.
  if (field.type === 'signature') {
    return <SignaturePad field={field} value={value} onChange={onChange} lang={lang} />
  }

  // A pass/fail grid — the paper form this replaces, one row per item. Self-contained (like
  // section_note above) instead of falling through to the shared wrapper below: that wrapper's
  // <label htmlFor={field.key}> points at an id no single control owns, and each row needs its own
  // line so the tap targets can run the full width of a ~360px phone.
  if (field.type === 'checklist') {
    const rows = Array.isArray(field.rows) ? field.rows : []
    const opts = tOptions(field, lang)
    const answers = value && typeof value === 'object' && !Array.isArray(value) ? value : {}
    const answered = rows.filter((r) => answers[r.key] !== undefined && answers[r.key] !== '').length
    const pick = (rowKey, val) => {
      // An unanswered row is ABSENT, not blank. Leaving a '' behind would mean a grid the person tapped
      // and then cleared no longer reads as untouched: the server would answer "1 of 18 items still
      // needs an answer" where it should simply say the field is required.
      const next = { ...answers }
      if (next[rowKey] === val) delete next[rowKey]
      else next[rowKey] = val
      onChange(next)
    }
    return (
      <div role="group" aria-label={tLabel(field, lang)} style={{ marginBottom: 16 }}>
        <div style={label}>
          {tLabel(field, lang)}{req && <span style={{ color: '#b91c1c' }}> *</span>}
        </div>
        {/* This page runs NO client-side validation — it relies entirely on the server's 422 — so the
            counter is the only signal the person has that Send will go through. Built by the
            dictionary rather than interpolated here: the two numbers do not have to fall in the same
            order in every language. */}
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
          {t('answeredCount', answered, rows.length)}
        </div>
        {tHelp(field, lang) && (
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>{tHelp(field, lang)}</div>
        )}
        {rows.map((r) => (
          <div key={r.key} style={{ padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 16, color: '#334155', marginBottom: 6 }}>{tRow(field, r, lang)}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {opts.map((o) => {
                const on = answers[r.key] === o.value
                return (
                  // type="button" is load-bearing: this renders inside <form onSubmit={submit}>, where a
                  // bare <button> defaults to submit — tapping OK on item 1 would send the inspection.
                  // The button READS in the chosen language and its VALUE is untouched — `defect`
                  // stays `defect` under a button that says "Falla". That is the whole reason a
                  // sheet filled in Spanish stores the same record as one filled in English.
                  <button key={o.value} type="button" aria-pressed={on} style={{ ...choice, ...(on ? choiceOn : null) }}
                          onClick={() => pick(r.key, o.value)}>
                    {o.label}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    )
  }

  let control
  switch (field.type) {
    case 'long_text':
      control = <textarea {...common} rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      break
    case 'integer':
    case 'decimal':
      control = (
        <input {...common} type="number" inputMode="decimal"
               step={field.type === 'integer' ? '1' : 'any'}
               value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      )
      break
    case 'date':
      control = <input {...common} type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} />
      break
    case 'time':
      control = <input {...common} type="time" value={value || ''} onChange={(e) => onChange(e.target.value)} />
      break
    case 'checkbox':
      control = (
        <input id={field.key} type="checkbox" style={{ width: 22, height: 22 }}
               checked={!!value} onChange={(e) => onChange(e.target.checked)} />
      )
      break
    case 'single_select':
      control = (
        <select {...common} value={value || ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">{t('choose')}</option>
          {tOptions(field, lang).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )
      break
    case 'multi_select':
      control = (
        <div>
          {tOptions(field, lang).map((o) => {
            const on = Array.isArray(value) && value.includes(o.value)
            return (
              <label key={o.value} style={{ display: 'block', padding: '8px 0', fontSize: 16 }}>
                <input type="checkbox" checked={on} style={{ width: 20, height: 20, marginRight: 8 }}
                       onChange={() => {
                         const next = new Set(Array.isArray(value) ? value : [])
                         if (on) next.delete(o.value)
                         else next.add(o.value)
                         onChange([...next])
                       }} />
                {o.label}
              </label>
            )
          })}
        </div>
      )
      break
    case 'photo':
    case 'attachment':
      control = (
        // `capture` opens the camera straight away on a phone; `multiple` is what makes several
        // photos per submission possible, which the owner asked for.
        <input id={field.key} type="file" accept="image/*,application/pdf" multiple capture="environment"
               style={{ ...input, padding: 10 }}
               onChange={(e) => onFiles(field.key, [...(e.target.files || [])])} />
      )
      break
    default:
      control = <input {...common} value={value || ''} onChange={(e) => onChange(e.target.value)} />
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor={field.key} style={label}>
        {tLabel(field, lang)}{req && <span style={{ color: '#b91c1c' }}> *</span>}
      </label>
      {control}
      {/* Said BEFORE the form's own help, because "why can't I type here" is the more urgent
          question of the two, and on the starter sheet the field's own help ("auto when the asset
          tag is scanned — otherwise pick from the fleet register") describes the other half. */}
      {locked && (
        <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>{t('fromTheSticker')}</div>
      )}
      {mustFill && (
        <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>{t('blankStickerHelp')}</div>
      )}
      {tHelp(field, lang) && (
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{tHelp(field, lang)}</div>
      )}
    </div>
  )
}

/**
 * The language control. Above everything, including the identity form, because the person who needs it
 * cannot read the identity form.
 *
 * One button per language, each labelled IN ITS OWN LANGUAGE — never `EN`/`ES` (a code is not a word)
 * and never a flag (a flag is a country; a Mexican hand and a Salvadoran hand share no flag). It reuses
 * the sheet's own tap discipline verbatim: `minorButton`'s 44px iOS target floor and 16px no-zoom
 * floor, and `choiceOn`'s filled selected state, which is readable in direct sun on a smeared screen
 * where a border tint is not.
 */
function LanguageBar({ languages, lang, onPick }) {
  if (!languages || languages.length < 2) return null
  return (
    <div role="group" aria-label={chrome(lang)('language')}
         style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
      {languages.map((tag) => (
        <button key={tag} type="button" lang={tag} aria-pressed={tag === lang}
                style={{ ...minorButton, flex: 1, ...(tag === lang ? choiceOn : null) }}
                onClick={() => onPick(tag)}>
          {languageName(tag)}
        </button>
      ))}
    </div>
  )
}

export default function PublicInspection() {
  const { tenantId, code } = useParams()
  const [sheet, setSheet] = useState(null)
  // Errors are held as {key} (one of ours) or {detail} (the server's prose), never as finished text.
  // Holding the finished string would freeze the message in whatever language was selected when it
  // happened, so switching language would leave a Spanish page with an English error on it.
  const [loadError, setLoadError] = useState(null)
  const [badge, setBadge] = useState('')
  const [pin, setPin] = useState('')
  const [typedName, setTypedName] = useState('')
  const [session, setSession] = useState('')
  const [signer, setSigner] = useState('')
  const [answers, setAnswers] = useState({})
  const [files, setFiles] = useState({})           // { field_key: File[] }
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [missing, setMissing] = useState([])
  const [done, setDone] = useState(null)
  // Set when a photo pick had to be trimmed to fit the form-wide cap. Separate from `error`: the
  // submission is still fine and the person can carry on — this only says what was NOT attached,
  // which is the fact a silent `.slice()` used to withhold.
  const [photoNotice, setPhotoNotice] = useState(null)
  // An explicit choice made on THIS visit. Null means "nobody has chosen yet", which is what lets the
  // priority order below (remembered choice -> phone -> tenant default) still apply.
  const [chosen, setChosen] = useState(null)
  // "This label is on the wrong machine." Open panel / in flight / said.
  const [reporting, setReporting] = useState(false)
  const [reportText, setReportText] = useState('')
  const [reported, setReported] = useState(false)
  const topRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    fetch(`${API_BASE}/public/inspection/${encodeURIComponent(tenantId)}/${encodeURIComponent(code)}`)
      .then(async (r) => {
        if (!r.ok) {
          const detail = (await r.json().catch(() => ({}))).detail
          throw Object.assign(new Error('load'), {
            info: typeof detail === 'string' && detail ? { detail } : { key: 'stickerInvalid' },
          })
        }
        return r.json()
      })
      .then((d) => {
        if (cancelled) return
        setSheet(d)
        // THE MACHINE, seeded. This is the owner's report — "it did not auto load the equipment info
        // that the sticker belonged to" — and it is one line because the server does the deciding:
        // `prefill` already names the boxes and their values, so the page never has to guess which
        // field means "which machine". A sticker is bound to ONE machine, so the sheet it opens has
        // to arrive knowing it; otherwise the label taught the person nothing and they can file a
        // signed inspection against equipment nobody looked at.
        //
        // Seeded rather than rendered from `sheet` directly so it travels in the payload like any
        // other answer, which is what makes the PDF, the index and the write-back all agree.
        if (d && d.prefill && Object.keys(d.prefill).length) {
          setAnswers((a) => ({ ...d.prefill, ...a }))
        }
      })
      .catch((e) => { if (!cancelled) setLoadError(e.info || { key: 'stickerInvalid' }) })
    return () => { cancelled = true }
  }, [tenantId, code])

  /**
   * Which languages the toggle offers.
   *
   * The union of what the SHEET is written in and what this PAGE speaks, and the union is deliberate.
   * The chrome dictionary is ours and always available; the form content is the tenant's. A tenant who
   * has not translated their sheet still gets "¿Quién es usted? / Número de gafete / Enviar" in
   * Spanish with the safety questions in English — worse than a fully translated sheet, and much
   * better than nothing, because the fallback is total: every untranslated string shows its English,
   * never a blank and never a key.
   */
  const languages = useMemo(() => {
    const sheetLangs = bundleLanguages(sheet?.translations, sheet?.base_language)
    const base = sheetLangs[0]
    const offered = new Set([...sheetLangs, ...Object.keys(CHROME)])
    return [base, ...[...offered].filter((x) => x !== base).sort()]
  }, [sheet])

  const lang = useMemo(
    () => chosen || pickLanguage({ available: languages, tenantDefault: sheet?.default_language }),
    [chosen, languages, sheet],
  )
  const t = useMemo(() => chrome(lang), [lang])
  /** An error, in the language on screen right now. */
  const say = useCallback(
    (e) => (e && e.detail ? serverMessage(e.detail, lang) : (e && e.key ? t(e.key) : '')),
    [lang, t],
  )
  const pickLang = useCallback((tag) => { setChosen(tag); rememberLanguage(tag) }, [])

  /**
   * THIS SHEET'S LANGUAGE IS THE PAGE'S LANGUAGE, and it is not the one the application chain
   * resolves — that chain reads an account nobody here has, and a company setting that governs people
   * who sign in. So `<html lang>` said `en` while a hand read Spanish, for the whole visit, because
   * nothing on this page ever writes the application's own key. Without it a screen reader announces
   * these questions with English phonemes, to the one population that cannot fall back to reading them.
   *
   * Claimed rather than assigned, and released on the way out: see shared/i18n/documentLanguage.js.
   */
  useEffect(() => {
    claimDocumentLanguage(lang)
    return releaseDocumentLanguage
  }, [lang])

  const items = useMemo(
    () => renderablesOf(sheet?.schema, sheet?.translations)
      .filter((el) => el.kind !== 'field' || RENDERABLE.has(el.type)),
    [sheet],
  )
  const fields = useMemo(() => items.filter((el) => el.kind === 'field'), [items])
  const photoKeys = useMemo(
    () => fields.filter((f) => f.type === 'photo' || f.type === 'attachment').map((f) => f.key),
    [fields],
  )

  const identify = useCallback(async (e) => {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      const r = await fetch(
        `${API_BASE}/public/inspection/${encodeURIComponent(tenantId)}/${encodeURIComponent(code)}/identify`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ badge_number: badge, pin, name: typedName }) })
      const body = await r.json().catch(() => ({}))
      if (!r.ok) {
        throw Object.assign(new Error('identify'), {
          info: typeof body.detail === 'string' && body.detail
            ? { detail: body.detail } : { key: 'identifyFailed' },
        })
      }
      setSession(body.session)
      setSigner(body.signer_name || '')
      setPin('')                     // never leave the credential sitting in component state
    } catch (err) {
      setError(err.info || { key: 'identifyFailed' })
    } finally {
      setBusy(false)
    }
  }, [tenantId, code, badge, pin, typedName])

  /**
   * "This label is on the wrong machine."
   *
   * NOT a way to change the machine — that is the whole design decision. A hand who can retype the
   * unit can file a signed pre-operation inspection against equipment nobody looked at, which is the
   * exact failure the label exists to prevent. So the escape hatch points at the office, who are the
   * only people who can actually fix it by peeling the label off. The sheet being filled in is
   * untouched: it still records what the label said, which is what makes the report evidence rather
   * than a correction.
   */
  const reportWrongMachine = useCallback(async (e) => {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      const r = await fetch(
        `${API_BASE}/public/inspection/${encodeURIComponent(tenantId)}/${encodeURIComponent(code)}/wrong-machine`,
        { method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Forms-Public-Session': session },
          body: JSON.stringify({ says_machine: reportText }) })
      if (!r.ok) {
        const detail = (await r.json().catch(() => ({}))).detail
        throw Object.assign(new Error('report'), {
          info: typeof detail === 'string' && detail ? { detail } : { key: 'reportFailed' },
        })
      }
      setReported(true)
      setReporting(false)
    } catch (err) {
      setError(err.info || { key: 'reportFailed' })
    } finally {
      setBusy(false)
    }
  }, [tenantId, code, session, reportText])

  const submit = useCallback(async (e) => {
    e.preventDefault()
    setBusy(true); setError(null); setMissing([])
    try {
      const fd = new FormData()
      // The payload is keyed by field key and row key, and its values are option VALUES. Nothing about
      // the language on screen is in here, or anywhere else in this request — which is what makes "the
      // same record whichever language it was filled in" structural rather than merely tested.
      fd.append('payload', JSON.stringify(answers))
      const names = []
      photoKeys.forEach((key) => (files[key] || []).forEach((f) => { fd.append('files', f); names.push(key) }))
      fd.append('photo_fields', names.join(','))
      const r = await fetch(
        `${API_BASE}/public/inspection/${encodeURIComponent(tenantId)}/${encodeURIComponent(code)}/submit`,
        { method: 'POST', headers: { 'X-Forms-Public-Session': session }, body: fd })
      const body = await r.json().catch(() => ({}))
      if (!r.ok) {
        if (r.status === 422 && body.detail && body.detail.missing_required) {
          setMissing(body.detail.missing_required)
          throw Object.assign(new Error('422'), { info: { key: 'answersStillNeeded' } })
        }
        if (r.status === 401) {
          setSession('')
          throw Object.assign(new Error('401'), { info: { key: 'sessionExpired' } })
        }
        throw Object.assign(new Error('submit'), {
          info: typeof body.detail === 'string' && body.detail
            ? { detail: body.detail } : { key: 'sendFailed' },
        })
      }
      setDone(body)
    } catch (err) {
      setError(err.info || { key: 'sendFailed' })
      topRef.current?.scrollIntoView({ behavior: 'smooth' })
    } finally {
      setBusy(false)
    }
  }, [tenantId, code, session, answers, files, photoKeys])

  if (loadError) {
    // The toggle appears here too. Someone whose sticker has been revoked still has to be able to read
    // WHY, and this screen is reachable before any sheet has loaded.
    return (
      <div style={wrap} lang={lang}>
        <LanguageBar languages={languages} lang={lang} onPick={pickLang} />
        <div style={card}>
          <h2 style={{ marginTop: 0 }}>{t('stickerNotRecognised')}</h2>
          <p>{say(loadError)}</p>
        </div>
      </div>
    )
  }
  if (!sheet) return <div style={wrap} lang={lang}><p>{t('loading')}</p></div>

  const formName = tDocumentName(documentNode(sheet.translations), lang, sheet.form.name)

  if (done) {
    return (
      <div style={wrap} lang={lang}>
        <LanguageBar languages={languages} lang={lang} onPick={pickLang} />
        <div style={{ ...card, borderColor: '#16a34a' }}>
          <h2 style={{ marginTop: 0 }}>{t('sent')}</h2>
          <p>{sheet.unit.label}</p>
          {done.record_number && <p style={{ fontSize: 22, fontWeight: 700 }}>#{done.record_number}</p>}
          <p style={{ color: '#475569' }}>{t('closePage')}</p>
        </div>
      </div>
    )
  }

  const mode = sheet.identity_mode
  // A BLANK sticker (the rental case) binds a sheet and no machine: it lives in the job box or on the
  // back of a badge and the hand types the machine himself. Everything conditional below hangs off
  // this one fact, and the server is what decides it.
  const blank = sheet.sticker_kind === 'blank'
  const lockedKeys = Array.isArray(sheet.locked) ? sheet.locked : []
  const unitKeys = Array.isArray(sheet.unit_fields) ? sheet.unit_fields : []

  return (
    /* ⚠ `lang` ON THE PAGE ITSELF, because `<html lang>` is not this page's to set. That attribute is
       written by LanguageProvider from the APPLICATION's chain — an account preference, the login
       toggle, the app's tenant default — and this sheet's language is decided by an entirely
       different question (`pickLanguage`, over the languages THIS VERSION is written in, defaulted by
       the tenant's `public_default_language`). The two legitimately disagree, and when they did, a
       screen reader pronounced Spanish safety questions with English phonemes for the one population
       that has no other way to read them. A `lang` on the subtree is what the attribute is for. */
    <div style={wrap} ref={topRef} lang={lang}>
      {/* FIRST on the page, above the equipment card and above the identity form. The constraint that
          puts it here: the person who needs this control cannot read the form that would otherwise
          come first, and they have no account whose preference could have been consulted. */}
      <LanguageBar languages={languages} lang={lang} onPick={pickLang} />

      <div style={card}>
        {/* The machine, named. This is the one thing a bound sticker is for. On a blank one the same
            place carries what the OFFICE calls the sticker ("Job box — rentals"), because that is the
            only true thing there is to say: this label is not on any one machine. */}
        <div style={{ fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>
          {blank ? t('sticker') : t('equipment')}
        </div>
        <div style={{ fontSize: 20, fontWeight: 700 }}>{sheet.unit.label}</div>
        {blank && (
          <div style={{ marginTop: 6, color: '#475569', fontSize: 14 }}>{t('blankSticker')}</div>
        )}
        <div style={{ marginTop: 8, color: '#475569' }}>{formName}</div>

        {/* The escape hatch for a label on the wrong truck. Only once the person has proved who they
            are: without that this is a row-writing primitive for anyone who photographs a sticker,
            and an anonymous report is not one the office can follow up anyway. Never offered on a
            blank sticker — there is no label-to-machine claim there to be wrong about. */}
        {!blank && session && !reported && !reporting && (
          <button type="button" style={{ ...minorButton, marginTop: 12 }}
                  onClick={() => setReporting(true)}>
            {t('wrongMachine')}
          </button>
        )}
        {reported && (
          <div style={{ marginTop: 12, color: '#166534', fontSize: 14 }}>{t('reportSent')}</div>
        )}
      </div>

      {reporting && (
        <form style={{ ...card, borderColor: '#b45309' }} onSubmit={reportWrongMachine}>
          <h3 style={{ marginTop: 0 }}>{t('wrongMachineTitle')}</h3>
          <p style={{ color: '#475569' }}>{t('wrongMachineBody')}</p>
          <label style={label} htmlFor="wrong-machine">{t('whatMachine')}</label>
          <textarea id="wrong-machine" style={input} rows={3} value={reportText}
                    onChange={(e) => setReportText(e.target.value)} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="button" style={{ ...minorButton, flex: 1 }}
                    onClick={() => setReporting(false)}>{t('clear')}</button>
            <button style={{ ...button, flex: 1, width: 'auto' }} disabled={busy}>
              {t('tellTheOffice')}
            </button>
          </div>
        </form>
      )}

      {error && (
        <div style={{ ...card, borderColor: '#b91c1c', color: '#b91c1c' }}>{say(error)}</div>
      )}

      {photoNotice && (
        <div style={{ ...card, borderColor: '#d97706', color: '#92400e' }} role="status">{photoNotice}</div>
      )}

      {!session ? (
        <form style={card} onSubmit={identify}>
          <h3 style={{ marginTop: 0 }}>{t('whoAreYou')}</h3>
          {mode === 'typed_name' ? (
            <div style={{ marginBottom: 16 }}>
              <label style={label} htmlFor="name">{t('yourName')}</label>
              <input id="name" style={input} value={typedName} onChange={(e) => setTypedName(e.target.value)}
                     autoComplete="off" />
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={label} htmlFor="badge">{t('badgeNumber')}</label>
                <input id="badge" style={input} value={badge} onChange={(e) => setBadge(e.target.value)}
                       autoComplete="off" autoCapitalize="characters" />
              </div>
              {mode === 'badge_pin' && (
                <div style={{ marginBottom: 16 }}>
                  <label style={label} htmlFor="pin">{t('pin')}</label>
                  <input id="pin" style={input} type="password" inputMode="numeric" value={pin}
                         onChange={(e) => setPin(e.target.value)} autoComplete="off" />
                </div>
              )}
            </>
          )}
          <button style={button} disabled={busy}>{busy ? t('checking') : t('continue')}</button>
        </form>
      ) : (
        <form style={card} onSubmit={submit}>
          {signer && (
            <p style={{ marginTop: 0, color: '#475569' }}>{t('signedInAs')} <strong>{signer}</strong></p>
          )}
          {items.map((el, idx) => (el.kind === 'text' ? (
            // A design element carries no key of its own on the v1 path, so the index is the only
            // stable identity available — and this list is rebuilt only when the schema changes.
            <TextBlock key={el.id || `text-${idx}`} element={el} lang={lang} />
          ) : (
            <div key={el.key} style={missing.includes(el.key)
              ? { borderLeft: '3px solid #b91c1c', paddingLeft: 10, marginLeft: -13 } : undefined}>
              <Field
                field={el}
                lang={lang}
                locked={lockedKeys.includes(el.key)}
                mustFill={blank && unitKeys.includes(el.key)}
                value={answers[el.key]}
                // The guard is here rather than only on the input's `readOnly`, because readOnly is a
                // property of a rendered control and this is a property of the ANSWER: whatever a
                // future widget, an autofill, or a devtools poke does, a locked key cannot move. The
                // server overwrites it either way (sticker_submit), so this only ever decides what the
                // person is shown — but showing them an edit that will be silently reverted on the
                // record is worse than not accepting it.
                onChange={(v) => {
                  if (lockedKeys.includes(el.key)) return
                  setAnswers((a) => ({ ...a, [el.key]: v }))
                }}
                // THE CAP IS ON THE WHOLE SUBMISSION, SO IT IS COUNTED ON THE WHOLE SUBMISSION.
                // This used to be `list.slice(0, sheet.max_photos)` — per FIELD, while the server
                // (`public_inspection.py:373`) caps the TOTAL across every field, because `submit`
                // above flattens all of them into one `files` list. On a sheet with four photo
                // fields and a cap of six, two fields of four passed the client and then 400'd the
                // ENTIRE submission — after somebody had filled in a whole inspection on a phone.
                // And the server's good sentence ("You can attach at most 6 photos.") never
                // reached them, because the client had already silently dropped the overflow.
                //
                // Now: count what is already attached elsewhere, keep what fits, and SAY what did
                // not fit. A silent `.slice()` is the same untruth in miniature — the person
                // believes they attached eight photos and eight were not sent.
                onFiles={(k, list) => setFiles((s) => {
                  const usedElsewhere = photoKeys.reduce(
                    (n, key) => (key === k ? n : n + (s[key]?.length || 0)), 0)
                  const room = Math.max(0, sheet.max_photos - usedElsewhere)
                  const kept = list.slice(0, room)
                  setPhotoNotice(
                    kept.length < list.length
                      ? t('tooManyPhotos', { max: sheet.max_photos, dropped: list.length - kept.length })
                      : null)
                  return { ...s, [k]: kept }
                })}
              />
            </div>
          )))}
          <button style={button} disabled={busy}>{busy ? t('sending') : t('send')}</button>
        </form>
      )}
    </div>
  )
}
